import { useState } from "react";
import { useCurrentPlanLabel } from "../../shared/plan/useCurrentPlan";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Clock, Loader2, Wallet } from "lucide-react";

import {
  saasBillingApi,
  saasBillingQueryKeys,
  type SaasBillingSummary,
  type SaasPaymentIntent,
  type WalletEntry,
} from "../../shared/api/saasBilling";
import { apiError } from "../../shared/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

const VND = new Intl.NumberFormat("vi-VN");

/** Chỉ dùng khi chưa tải xong tóm tắt — con số thật đến từ máy chủ. */
const MIN_TOP_UP_FALLBACK = 10_000;

/** Gợi ý nạp nhanh — khớp giá gói để khách không phải tự tính. */
const QUICK_AMOUNTS = [1_500_000, 4_500_000, 9_000_000];

/**
 * Cài đặt → Ví & Thanh toán: tiền phòng khám trả cho Addy (#18974).
 *
 * <p>Không phải hoá đơn viện phí của bệnh nhân — cái đó ở /invoices. Hai loại tiền, hai ví.
 *
 * <p>Trang này cố tình phân biệt <b>"đã nhận tiền"</b> và <b>"đã kích hoạt"</b>. Tiền có thể đã rời
 * tài khoản khách mà chưa thành số dư (webhook chưa về), hoặc đã thành số dư mà chưa thành gói
 * (nạp rồi chưa bấm mua). Gộp hai thứ đó thành một dòng "trạng thái" là cách chắc chắn nhất để
 * nhận cuộc gọi "tôi đã chuyển tiền rồi mà".
 */
export function BillingSettingsPage() {
  const qc = useQueryClient();
  const planBadge = useCurrentPlanLabel();
  const summary = useQuery({
    queryKey: saasBillingQueryKeys.summary(),
    queryFn: () => saasBillingApi.summary(),
  });
  const transactions = useQuery({
    queryKey: saasBillingQueryKeys.transactions(),
    queryFn: () => saasBillingApi.transactions(),
  });
  const payments = useQuery({
    queryKey: saasBillingQueryKeys.payments(),
    queryFn: () => saasBillingApi.payments(),
  });

  const [amount, setAmount] = useState<string>("");
  const typedVnd = Number(amount.replace(/\D/g, "")) || 0;
  /** Máy chủ quyết mức tối thiểu; trang chỉ hiển thị lại. */
  const minTopUp = summary.data?.minTopUpVnd ?? MIN_TOP_UP_FALLBACK;

  const topUp = useMutation({
    mutationFn: (vnd: number) => saasBillingApi.topUp(vnd),
    onSuccess: (intent) => {
      void qc.invalidateQueries({ queryKey: saasBillingQueryKeys.payments() });
      if (intent.checkoutUrl) {
        window.open(intent.checkoutUrl, "_blank", "noopener");
      }
    },
  });

  if (summary.isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-7 w-64" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  const s = summary.data;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-bold text-foreground">Ví &amp; Thanh toán</h1>
        <p className="mt-1 max-w-prose text-sm text-muted-foreground">
          Tiền phòng khám trả cho Addy: nạp ví, mua và gia hạn gói. Không liên quan tới hoá đơn
          viện phí của bệnh nhân.
        </p>
      </div>

      {summary.isError && (
        <p className="text-sm text-destructive">{apiError(summary.error).detail}</p>
      )}

      {s && <StateBanner summary={s} />}

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-lg border p-4">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <Wallet className="h-3.5 w-3.5" /> Số dư ví
          </div>
          <div className="mt-1 text-2xl font-bold tabular-nums text-foreground">
            {VND.format(s?.balanceVnd ?? 0)}đ
          </div>
          {s && s.pendingTopUpVnd > 0 && (
            <p className="mt-1 text-xs text-amber-700">
              Đang chờ về: <b>{VND.format(s.pendingTopUpVnd)}đ</b> — đã tạo lệnh nạp nhưng chưa
              nhận được xác nhận từ cổng thanh toán.
            </p>
          )}
          {s && !s.reconciled && (
            <p className="mt-1 flex items-start gap-1 text-xs text-destructive">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Sổ giao dịch và số dư đang lệch nhau. Addy đã được báo; vui lòng đối chiếu lịch sử
              bên dưới trước khi dựa vào con số này.
            </p>
          )}
        </div>

        <div className="rounded-lg border p-4">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Gói hiện tại
          </div>
          {/*
            TÊN gói, không phải mã nội bộ (#19596). Màn này từng in thẳng `planCode` nên nó hiện
            "PRO" trong khi `/settings/subscription` hiện "Chuyên nghiệp" cho cùng một gói — và
            chính hợp đồng của `PlanSummary.name` đã viết rằng `code` chỉ để gửi lại máy chủ.
            Chưa biết thì "—", không đoán.
          */}
          <div className="mt-1 text-2xl font-bold text-foreground">{planBadge ?? "—"}</div>
          <p className="mt-1 text-xs text-muted-foreground">
            {s?.activeTo
              ? `Hiệu lực tới ${new Date(s.activeTo).toLocaleDateString("vi-VN")}`
              : "Không có ngày hết hạn"}
          </p>
        </div>
      </div>

      <form
        className="rounded-lg border p-4"
        onSubmit={(e) => {
          e.preventDefault();
          // Không còn `if (vnd >= MIN) mutate(vnd)` rồi thôi: dưới mức tối thiểu, nút bấm KHÔNG
          // LÀM GÌ VÀ KHÔNG NÓI GÌ, nên người dùng đinh ninh hệ thống hỏng. Giờ nút bị tắt và có
          // dòng chữ nói rõ vì sao.
          if (typedVnd >= minTopUp) topUp.mutate(typedVnd);
        }}
      >
        <h2 className="text-sm font-semibold text-foreground">Nạp tiền vào ví</h2>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {QUICK_AMOUNTS.map((v) => (
            <Button
              key={v}
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setAmount(String(v))}
            >
              {VND.format(v)}đ
            </Button>
          ))}
          <Input
            className="w-44"
            inputMode="numeric"
            placeholder={`Tối thiểu ${VND.format(minTopUp)}đ`}
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))}
          />
          <Button
            type="submit"
            size="sm"
            disabled={topUp.isPending || typedVnd < minTopUp}
            title={
              typedVnd > 0 && typedVnd < minTopUp
                ? `Số tiền nạp tối thiểu là ${VND.format(minTopUp)}đ`
                : undefined
            }
          >
            {topUp.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
            Nạp tiền
          </Button>
        </div>
        {typedVnd > 0 && typedVnd < minTopUp && (
          <p className="mt-2 text-sm text-amber-700">
            Số tiền nạp tối thiểu là <b>{VND.format(minTopUp)}đ</b>. Bạn đang nhập{" "}
            {VND.format(typedVnd)}đ.
          </p>
        )}
        {topUp.isError && (
          <p className="mt-2 text-sm text-destructive">{apiError(topUp.error).detail}</p>
        )}
        <p className="mt-2 max-w-prose text-xs text-muted-foreground">
          Bấm Nạp tiền sẽ mở trang thanh toán ở tab mới. <b>Không cần quay lại trang này</b>: nếu
          bạn đóng tab giữa chừng, tiền vẫn được ghi nhận khi cổng thanh toán xác nhận, và số dư sẽ
          tự cập nhật.
        </p>
      </form>

      <PaymentsTable payments={payments.data ?? []} />
      <TransactionsTable entries={transactions.data ?? []} />
    </div>
  );
}

/** Băng trạng thái — chỉ hiện khi có chuyện cần nói. */
function StateBanner({ summary }: { summary: SaasBillingSummary }) {
  if (summary.billingState === "GRACE") {
    return (
      <div className="flex items-start gap-2 rounded-md border border-amber-600/30 bg-amber-600/5 p-3 text-sm">
        <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <div>
          <b>Gói đã hết hạn — đang trong thời gian ân hạn</b>
          {summary.graceUntil && (
            <> tới {new Date(summary.graceUntil).toLocaleDateString("vi-VN")}</>
          )}
          . Mọi chức năng khám chữa bệnh vẫn hoạt động bình thường;{" "}
          <b>chỉ tin nhắn SMS và Zalo tạm ngưng</b>. Gia hạn để mở lại.
        </div>
      </div>
    );
  }
  if (summary.billingState === "READ_ONLY") {
    return (
      <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
        <div>
          <b>Tài khoản đang ở chế độ chỉ xem.</b> Bạn vẫn xem và xuất được toàn bộ dữ liệu của
          phòng khám — <b>không có dữ liệu nào bị xoá</b> — nhưng chưa nhập liệu mới được. Nạp tiền
          và gia hạn ngay tại trang này để mở lại.
        </div>
      </div>
    );
  }
  return null;
}

function PaymentsTable({ payments }: { payments: SaasPaymentIntent[] }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  const cancel = useMutation({
    mutationFn: (id: string) => saasBillingApi.cancel(id),
    onSuccess: () => {
      setError(null);
      setConfirming(null);
      void qc.invalidateQueries({ queryKey: saasBillingQueryKeys.payments() });
      void qc.invalidateQueries({ queryKey: saasBillingQueryKeys.summary() });
    },
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  if (payments.length === 0) return null;
  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full min-w-[40rem] text-sm">
        <caption className="border-b px-3 py-2 text-left text-sm font-semibold text-foreground">
          Lệnh nạp tiền gần đây
        </caption>
        <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Thời gian</th>
            <th className="px-3 py-2 text-left font-medium">Số đơn</th>
            <th className="px-3 py-2 text-right font-medium">Số tiền</th>
            <th className="px-3 py-2 text-left font-medium">Trạng thái</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {payments.map((p) => (
            <tr key={p.id} className="border-t">
              <td className="px-3 py-2">{new Date(p.createdAt).toLocaleString("vi-VN")}</td>
              <td className="px-3 py-2 font-mono text-xs">{p.providerOrderCode}</td>
              <td className="px-3 py-2 text-right tabular-nums">{VND.format(p.amountVnd)}đ</td>
              <td className="px-3 py-2">{paymentStatusLabel(p.status)}</td>
              <td className="px-3 py-2 text-right">
                {p.status === "PENDING" && (
                  <div className="flex items-center justify-end gap-3">
                    {p.checkoutUrl && (
                      <a
                        className="text-primary underline"
                        href={p.checkoutUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Tiếp tục trả
                      </a>
                    )}
                    {confirming === p.id ? (
                      <span className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="destructive"
                          className="h-7"
                          disabled={cancel.isPending}
                          onClick={() => cancel.mutate(p.id)}
                        >
                          Xác nhận huỷ
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7"
                          onClick={() => setConfirming(null)}
                        >
                          Thôi
                        </Button>
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-muted-foreground"
                        onClick={() => {
                          setError(null);
                          setConfirming(p.id);
                        }}
                      >
                        Huỷ
                      </Button>
                    )}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {confirming && (
        <p className="border-t bg-amber-600/5 px-3 py-2 text-xs text-amber-700">
          Huỷ chỉ bỏ lệnh này khỏi danh sách chờ. <b>Nếu bạn đã chuyển khoản rồi thì tiền vẫn về
          và vẫn được cộng vào ví</b> — mã QR là một lệnh chuyển khoản, huỷ ở đây không nói gì với
          ngân hàng.
        </p>
      )}
      {error && <p className="border-t px-3 py-2 text-sm text-destructive">{error}</p>}
    </div>
  );
}

/**
 * Nhãn viết bằng lời khách hiểu, và mỗi nhãn nói được bước tiếp theo.
 *
 * RECEIVED_UNAPPLIED là nhãn quan trọng nhất: tiền ĐÃ tới nhưng hệ thống chưa ghi vào ví được.
 * Nói "thất bại" ở đây là sai và khách sẽ trả lần nữa.
 */
function paymentStatusLabel(status: SaasPaymentIntent["status"]) {
  switch (status) {
    case "PENDING":
      return <span className="text-amber-700">Chờ thanh toán</span>;
    case "PAID":
      return <span className="text-emerald-700">Đã cộng vào ví</span>;
    case "RECEIVED_UNAPPLIED":
      return (
        <span className="text-destructive">
          Đã nhận tiền, chưa cộng được — Addy đang xử lý
        </span>
      );
    case "CANCELLED":
      return <span className="text-muted-foreground">Đã huỷ</span>;
    case "EXPIRED":
      return <span className="text-muted-foreground">Hết hạn</span>;
    case "FAILED":
      return <span className="text-muted-foreground">Không tạo được link</span>;
    default:
      return status;
  }
}

function TransactionsTable({ entries }: { entries: WalletEntry[] }) {
  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full min-w-[44rem] text-sm">
        <caption className="border-b px-3 py-2 text-left text-sm font-semibold text-foreground">
          Lịch sử ví
        </caption>
        <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Thời gian</th>
            <th className="px-3 py-2 text-left font-medium">Nội dung</th>
            <th className="px-3 py-2 text-right font-medium">Số tiền</th>
            <th className="px-3 py-2 text-right font-medium">Số dư sau</th>
          </tr>
        </thead>
        <tbody>
          {entries.length === 0 ? (
            <tr>
              <td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">
                Chưa có giao dịch nào.
              </td>
            </tr>
          ) : (
            entries.map((e) => (
              <tr key={e.id} className="border-t">
                <td className="px-3 py-2">{new Date(e.createdAt).toLocaleString("vi-VN")}</td>
                <td className="px-3 py-2">
                  <div>{typeLabel(e.type)}</div>
                  {e.note && <div className="text-xs text-muted-foreground">{e.note}</div>}
                </td>
                <td
                  className={
                    e.amountVnd >= 0
                      ? "px-3 py-2 text-right tabular-nums text-emerald-700"
                      : "px-3 py-2 text-right tabular-nums text-foreground"
                  }
                >
                  {e.amountVnd >= 0 ? "+" : ""}
                  {VND.format(e.amountVnd)}đ
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                  {VND.format(e.balanceAfterVnd)}đ
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function typeLabel(type: WalletEntry["type"]) {
  switch (type) {
    case "TOP_UP":
      return "Nạp tiền";
    case "SUBSCRIPTION_CHARGE":
      return "Mua / gia hạn gói";
    case "SERVICE_CHARGE":
      return "Phí dịch vụ";
    case "REFUND_TO_WALLET":
      return "Hoàn vào ví";
    case "REFUND_TO_BANK":
      return "Hoàn về ngân hàng";
    // "Hoàn tiền" sẽ khiến khách đi tìm một giao dịch bị hoàn không tồn tại — Addy chưa bao giờ
    // thu khoản phí này, cổng trừ thẳng vào tiền đối soát của phòng khám.
    case "PAYMENT_FEE_REBATE":
      return "Addy bao phí cổng";
    case "MANUAL_ADJUSTMENT":
      return "Điều chỉnh bởi Addy";
    default:
      return type;
  }
}
